import {
  ChevronLeftIcon,
  ChevronRightIcon,
  FaroIconButton,
  FaroText,
  grey,
} from "@faro-lotv/flat-ui";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import { ButtonBase, alpha } from "@mui/material";
import { Stack } from "@mui/system";
import { type JSX } from "react";
import { HOVER_OPACITY } from "./calendar-styles";
import { CaptureGranularity } from "./capture-calendar-types";

type CalendarHeaderProps = {
  /** The month or year in sight */
  label: string;
  /** Id MUI links the day grid to, for accessibility */
  labelId?: string;
  /** The view the header belongs to */
  granularity: CaptureGranularity;
  /** Switch to the other view */
  onToggleView(): void;
  /** Page back one month or year */
  onPrevious(): void;
  /** Page forward one month or year */
  onNext(): void;
  /** True when paging back would leave the selectable span */
  isPreviousDisabled: boolean;
  /** True when paging forward would leave the selectable span */
  isNextDisabled: boolean;
};

/**
 * @returns The header both views share: the label that switches view on the left, the
 * paging arrows on the right
 */
export function CalendarHeader({
  label,
  labelId,
  granularity,
  onToggleView,
  onPrevious,
  onNext,
  isPreviousDisabled,
  isNextDisabled,
}: CalendarHeaderProps): JSX.Element {
  const isDayView = granularity === CaptureGranularity.day;
  const unit = isDayView ? "month" : "year";

  return (
    <Stack
      direction="row"
      sx={{ alignItems: "center", justifyContent: "space-between", mb: 0.5 }}
    >
      <ButtonBase
        aria-label={isDayView ? "show months" : "show days"}
        onClick={onToggleView}
        sx={{
          gap: 0.5,
          pl: 1,
          pr: 0.5,
          height: "28px",
          borderRadius: "4px",
          "&:hover": { backgroundColor: alpha(grey[500], HOVER_OPACITY) },
        }}
      >
        <FaroText id={labelId} variant="heading14" color={grey[100]}>
          {label}
        </FaroText>
        <ExpandMoreOutlinedIcon
          sx={{
            fontSize: "18px",
            color: grey[100],
            // Points down in the day view and up in the month view, as in MUI's pickers
            transform: isDayView ? "none" : "rotate(180deg)",
          }}
        />
      </ButtonBase>
      <Stack direction="row">
        <FaroIconButton
          aria-label={`previous ${unit}`}
          disabled={isPreviousDisabled}
          onClick={onPrevious}
          color={grey[100]}
          size="s"
        >
          <ChevronLeftIcon />
        </FaroIconButton>
        <FaroIconButton
          aria-label={`next ${unit}`}
          disabled={isNextDisabled}
          onClick={onNext}
          color={grey[100]}
          size="s"
        >
          <ChevronRightIcon />
        </FaroIconButton>
      </Stack>
    </Stack>
  );
}
